// src/App.js
import "./App.css";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { AuthProvider } from "./context/authContext";
import { store } from "./store";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { useEffect } from "react";
import { getProfile, setInitialized, selectAuth, selectIsInitialized } from "./store/slices/authSlice";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthRoute from "./components/AuthRoute";
import MainCustomLoader from "./components/MainCustomLoader";
import Home from "./pages/Home";
import ForexSignal from "./pages/ForexSignal";
import CryptoSignal from "./pages/CryptoSignal";
import Signup from "./pages/Signup";
import Signin from "./pages/Signin";
import Contact from "./pages/Contact";
import Subscribe from "./pages/Subscribe";
import PriceAlert from "./pages/PriceAlert";
import Dashboard from "./dashboard/Dashboard";
import Services from "./pages/Services";
import About from "./pages/About";
import VerifyEmail from "./pages/VerifyEmail";
import VerificationRequired from "./pages/VerificationRequired";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

function AppContent() {
  const dispatch = useAppDispatch();
  const { token, isAuthenticated, isLoading } = useAppSelector(selectAuth);
  const isInitialized = useAppSelector(selectIsInitialized);

  // Initialize auth on app load - ONLY ONCE
  useEffect(() => {
    const initAuth = async () => {
      // Skip if already initialized
      if (isInitialized) {
        return;
      }

      // If we have a token, try to get profile
      if (token && !isAuthenticated) {
        try {
          await dispatch(getProfile()).unwrap();
        } catch (error) {
          console.error('Auth initialization failed:', error);
          // Clear invalid token
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('user');
        }
      }
      
      // Mark as initialized
      dispatch(setInitialized());
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs once

  // Show loader while initializing or loading
  if (!isInitialized || isLoading) {
    return <MainCustomLoader />;
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/forex-signal" element={<ForexSignal />} />
          <Route path="/crypto-signal" element={<CryptoSignal />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/price-alert" element={<PriceAlert />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />

          {/* Email Verification Routes - Public */}
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-required" element={<VerificationRequired />} />

          {/* Password Reset Routes - Public */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Auth Routes - Only when NOT logged in */}
          <Route element={<AuthRoute />}>
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Signin />} />
          </Route>

          {/* Protected Routes - Only when logged in */}
          <Route element={<ProtectedRoute />}>
            {/* Dashboard route - all dashboard sections are handled inside */}
            <Route path="/dashboard" element={<Dashboard />} />
          </Route>

          {/* Fallback - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Provider>
  );
}

export default App;
